import type {
  TableHeaderCellProps as BaseTableHeaderCellProps,
  TableProps as BaseTableProps,
  TableEmptyStateProps as BaseTableEmptyStateProps,
} from '@cimpress-ui/react';
import type { RowData } from './tanstack.js';

declare module './tanstack.js' {
  export interface TableOptionsResolved<TData extends RowData> {
    /** Table options specific to Cimpress UI. */
    cimpressUi?: CimpressUiTableOptions<TData>;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface ColumnDefBase<TData extends RowData, TValue = unknown> extends CimpressUiTableColumnOptions {}
}

interface CimpressUiTableOptions<TData extends RowData> {
  /**
   * The number of rows in the full dataset, including headers.
   *
   * Provide this value if your table presents a subset of rows, such as when using pagination.
   * Use `-1` if the number of rows is not known.
   *
   * You must provide the `getRowNumber` function if `totalRowCount` is defined.
   */
  totalRowCount?: BaseTableProps['totalRowCount'];
  /**
   * A function that returns the row's position within the full dataset, including headers.
   *
   * Provide this function if your table presents a subset of rows, such as when using pagination.
   *
   * The returned number must be:
   * - greater than or equal to `1`
   * - less than or equal to the `totalRowCount`
   *
   * Use together with `totalRowCount` on the `Table` component.
   */
  getRowNumber?: (row: TData) => number;
  /** Configuration for an empty state shown when there's no data to display. */
  emptyState?: CimpressUiTableEmptyStateOptions;
  /** Configuration for a loading state shown when the data is being loaded into the table. */
  loadingState?: CimpressUiTableLoadingStateOptions;
}

interface CimpressUiTableColumnOptions {
  /**
   * Horizontal alignment of cell content within the column.
   * @default 'start'
   */
  contentAlignment?: BaseTableHeaderCellProps['columnContentAlignment'];
  /** The width of the column. Can be any valid value of the `width` CSS property. */
  width?: BaseTableHeaderCellProps['columnWidth'];
  /** An icon displayed before the header text. */
  headerIconStart?: BaseTableHeaderCellProps['iconStart'];
  /** An icon displayed after the header text. */
  headerIconEnd?: BaseTableHeaderCellProps['iconEnd'];
}

interface CimpressUiTableEmptyStateOptions extends Pick<BaseTableEmptyStateProps, 'title' | 'description' | 'action'> {}

interface CimpressUiTableLoadingStateOptions {
  /** Whether the table is currently in a loading state. */
  isLoading: boolean;
}
